import React, { useCallback, useState } from 'react';
import { Button, Flex, message, Typography } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { useSyncToVendorAppMutation } from '../../redux/api/order-line-item';

const { Text } = Typography;

const DOCUMENT_TYPES = {
	FAI: 'First Article Inspection',
	MATERIAL_CERTIFICATE: 'Material Certificate',
} as const;

const SYNC_MESSAGES = {
	success: 'FAI and Material Certificate synced to Vendor App.',
	partial: (okCount: number, failedTypes: string) =>
		`Synced ${okCount} of 2. Failed: ${failedTypes}.`,
	error: 'Sync to Vendor App failed.',
} as const;

type DocumentType = (typeof DOCUMENT_TYPES)[keyof typeof DOCUMENT_TYPES];

interface SyncResult {
	type: DocumentType;
	ok: boolean;
	error?: string;
}

interface SyncToVendorAppButtonProps {
	lineItemId: string;
	isDisabled?: boolean;
}

function getErrorMessage(error: unknown): string {
	if (error && typeof error === 'object' && 'data' in error) {
		const data = (error as { data?: { message?: string } }).data;
		if (data?.message) return data.message;
	}
	if (error instanceof Error) return error.message;
	return 'Failed';
}

function showSyncFeedback(results: SyncResult[]): void {
	const okCount = results.filter((r) => r.ok).length;
	const failed = results.filter((r) => !r.ok);

	if (failed.length === 0) {
		message.success(SYNC_MESSAGES.success);
		return;
	}
	if (okCount > 0) {
		message.warning(SYNC_MESSAGES.partial(okCount, failed.map((r) => r.type).join(', ')));
		return;
	}
	message.error(failed[0]?.error ?? SYNC_MESSAGES.error);
}

export const SyncToVendorAppButton: React.FC<SyncToVendorAppButtonProps> = ({
	lineItemId,
	isDisabled = false,
}) => {
	const [syncToVendorApp] = useSyncToVendorAppMutation();
	const [isSyncing, setIsSyncing] = useState(false);

	const syncDocumentType = useCallback(
		async (documentType: DocumentType): Promise<SyncResult> => {
			try {
				await syncToVendorApp({ id: lineItemId, document_type: documentType }).unwrap();
				return { type: documentType, ok: true };
			} catch (error) {
				return { type: documentType, ok: false, error: getErrorMessage(error) };
			}
		},
		[lineItemId, syncToVendorApp]
	);

	const handleSync = useCallback(async () => {
		setIsSyncing(true);
		try {
			const results = await Promise.all([
				syncDocumentType(DOCUMENT_TYPES.FAI),
				syncDocumentType(DOCUMENT_TYPES.MATERIAL_CERTIFICATE),
			]);
			showSyncFeedback(results);
		} catch {
			message.error(SYNC_MESSAGES.error);
		} finally {
			setIsSyncing(false);
		}
	}, [syncDocumentType]);

	const isButtonDisabled = isDisabled || isSyncing;

	return (
		<Flex vertical align="start">
			<Text>Sync to Vendor App</Text>
			<Button
				icon={<CloudUploadOutlined />}
				type="default"
				onClick={handleSync}
				loading={isSyncing}
				disabled={isButtonDisabled}
				aria-busy={isSyncing}
			>
				{isSyncing ? 'Syncing...' : 'Sync to Vendor App'}
			</Button>
		</Flex>
	);
};
